import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Utensils, Music, Sparkles, Star, Users, Coffee, Wine, Clock, MapPin } from "lucide-react";
import Hero from "../components/Hero";
import SectionHeading from "../components/SectionHeading";
import Reviews from "../components/Reviews";
import SplashFeature from "../components/SplashFeature";
import { Reveal } from "../hooks/useReveal";
import { BRAND, IMAGES, SERVICES, SIGNATURE_DISHES, SIGNATURE_COCKTAILS, GALLERY, HOURS, HAPPY_HOUR } from "../mock";
import { useReservation } from "../components/ReservationModal";

const SERVICE_ICONS = [Utensils, Music, Sparkles, Star, Coffee, Users];

export default function Home() {
  const { open } = useReservation();
  return (
    <div>
      <Hero />

      {/* Intro / Celebration */}
      <section className="relative py-24 md:py-32 bg-nala-black overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <p className="text-nala-gold tracking-luxe uppercase text-sm mb-5">Welcome to Nala</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight text-nala-cream mb-8">
              Where Atlanta Comes
              <span className="block text-gold-gradient">to Celebrate</span>
            </h2>
            <p className="font-serif-lux text-xl text-nala-cream-muted leading-relaxed mb-6">
              Nala Kitchen & Cocktails is Atlanta's premier dining and entertainment
              destination. With a vibrant atmosphere, elevated cuisine, and lively nightlife,
              we create unforgettable experiences.
            </p>
            <p className="font-serif-lux text-xl text-nala-cream-muted leading-relaxed mb-10">
              From brunch and day parties to exclusive VIP events, bottle service, and
              private rentals — Nala is your destination for any occasion.
            </p>
            <div className="flex flex-wrap gap-4">
              <button onClick={open} className="btn-gold">
                <span>Reservations</span>
              </button>
              <Link to="/private-events" className="btn-outline-gold">
                <span>Host a Private Event</span>
              </Link>
            </div>
          </Reveal>

          <Reveal delay={150} className="relative">
            <div className="relative grid grid-cols-2 gap-4">
              <img src={IMAGES.lobster} alt="Lobster" className="w-full h-72 object-cover mt-10 border border-nala-gold/20" />
              <img src={IMAGES.ckPurple} alt="Signature cocktail" className="w-full h-72 object-cover border border-nala-gold/20" />
              <img src={IMAGES.lamb} alt="Lamb" className="w-full h-72 object-cover -mt-6 border border-nala-gold/20" />
              <img src={IMAGES.ckSuperior1} alt="The Superior cocktail" className="w-full h-72 object-cover -mt-16 border border-nala-gold/20" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Services */}
      <section className="relative py-24 md:py-32 bg-nala-charcoal">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionHeading eyebrow="What We Offer" title="The Nala Experience" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-16">
            {SERVICES.map((s, i) => {
              const Icon = SERVICE_ICONS[i % SERVICE_ICONS.length];
              return (
                <Reveal key={s.title} delay={i * 70}>
                  <Link to={s.to} className="group block relative h-[420px] overflow-hidden border border-nala-gold/15">
                    <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-nala-black via-nala-black/50 to-transparent" />
                    <div className="absolute inset-0 p-8 flex flex-col justify-end">
                      <div className="w-11 h-11 rounded-full border border-nala-gold/50 flex items-center justify-center text-nala-gold mb-4 group-hover:bg-nala-gold group-hover:text-nala-black transition-all duration-500">
                        <Icon size={19} />
                      </div>
                      <h3 className="font-display text-2xl text-nala-cream mb-3">{s.title}</h3>
                      <p className="text-nala-cream-muted text-sm leading-relaxed mb-4 line-clamp-3">{s.desc}</p>
                      <span className="flex items-center gap-2 text-nala-gold text-xs uppercase tracking-[0.2em] font-medium">
                        {s.cta} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Signature Dishes */}
      <section className="relative py-24 md:py-32 bg-nala-black">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionHeading eyebrow={`Executive Chef · ${BRAND.chef}`} title="Signature Plates" subtitle="A fusion of bold flavors, fresh ingredients, and craftsmanship." />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-16">
            {SIGNATURE_DISHES.map((d, i) => (
              <Reveal key={d.name} delay={i * 80} className="group">
                <div className="relative overflow-hidden aspect-[3/4] border border-nala-gold/15">
                  <img src={d.image} alt={d.name} className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-nala-black/90 via-transparent to-transparent" />
                  <span className="absolute top-4 right-4 bg-nala-gold text-nala-black text-xs font-semibold px-3 py-1 tracking-wide-lux">{d.price}</span>
                </div>
                <h3 className="font-display text-lg text-nala-cream mt-5">{d.name}</h3>
                <p className="text-nala-cream-muted text-sm mt-1 font-serif-lux text-base">{d.desc}</p>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-14">
            <Link to="/menu" className="btn-gold"><span>View Full Menu</span></Link>
          </div>
        </div>
      </section>

      {/* Signature Cocktails band */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.ckSuperior4} alt="Signature cocktails" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-nala-black/85" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionHeading eyebrow="From the Bar" title="Signature Cocktails" subtitle="Expertly crafted by our mixologists — bold, balanced, unforgettable." />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
            {SIGNATURE_COCKTAILS.map((c, i) => (
              <Reveal key={c.name} delay={i * 80} className="bg-nala-charcoal/80 backdrop-blur-sm border border-nala-gold/20 overflow-hidden group">
                <div className="overflow-hidden aspect-square">
                  <img src={c.image} alt={c.name} className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-110" />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-display text-lg text-nala-cream">{c.name}</h3>
                    <span className="text-nala-gold font-semibold">{c.price}</span>
                  </div>
                  <p className="text-nala-cream-muted text-sm font-serif-lux text-base leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Drink Splatter Feature */}
      <SplashFeature />

      {/* Happy Hour */}
      <section className="relative py-24 md:py-32 bg-gradient-to-b from-nala-maroon-dark to-nala-charcoal">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <p className="text-nala-gold-light tracking-luxe uppercase text-sm mb-4">{HAPPY_HOUR.days} · {HAPPY_HOUR.time}</p>
            <h2 className="font-display text-5xl md:text-7xl text-gold-gradient">Happy Hour</h2>
            <div className="gold-divider mt-8"><span className="w-1.5 h-1.5 rotate-45 bg-nala-gold inline-block" /></div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8 mt-14 text-left">
            <Reveal className="bg-nala-black/40 border border-nala-gold/20 p-8">
              <h3 className="font-display text-xl text-nala-gold mb-5 uppercase tracking-wide-lux">Half Off Bites</h3>
              <ul className="space-y-2.5 text-nala-cream font-serif-lux text-lg">
                {HAPPY_HOUR.halfOff.map((f) => <li key={f}>{f}</li>)}
              </ul>
            </Reveal>
            <Reveal delay={100} className="bg-nala-black/40 border border-nala-gold/20 p-8">
              <h3 className="font-display text-xl text-nala-gold mb-5 uppercase tracking-wide-lux">Glasses</h3>
              <ul className="space-y-2.5 text-nala-cream">
                {HAPPY_HOUR.glasses.map((g) => (
                  <li key={g.name} className="flex justify-between border-b border-white/8 pb-2">
                    <span className="font-serif-lux text-lg">{g.name}</span>
                    <span className="text-nala-gold">{g.price}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={200} className="bg-nala-black/40 border border-nala-gold/20 p-8">
              <h3 className="font-display text-xl text-nala-gold mb-5 uppercase tracking-wide-lux">Shots & Cocktails</h3>
              <ul className="space-y-2.5 text-nala-cream">
                {HAPPY_HOUR.shots.map((s) => (
                  <li key={s.name} className="flex justify-between border-b border-white/8 pb-2">
                    <span className="font-serif-lux text-lg">{s.name}</span>
                    <span className="text-nala-gold">{s.price}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="relative py-24 md:py-32 bg-nala-black">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionHeading eyebrow="A Taste of Nala" title="The Gallery" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-16">
            {GALLERY.map((g, i) => (
              <Reveal key={i} delay={i * 50} className={`group relative overflow-hidden ${i % 5 === 0 ? "md:row-span-2 md:col-span-2 aspect-square md:aspect-auto" : "aspect-square"}`}>
                <img src={g.src} alt={g.label} className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-110" />
                <div className="absolute inset-0 bg-nala-black/0 group-hover:bg-nala-black/50 transition-colors duration-500 flex items-end p-5">
                  <span className="text-nala-cream font-display text-sm tracking-wide-lux opacity-0 group-hover:opacity-100 translate-y-3 group-hover:translate-y-0 transition-all duration-500">{g.label}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Reviews />
    </div>
  );
}
